// format.ts — prezentacja liczb. Jedyne miejsce, w którym grosze zamieniają się na złote.
//
// Grosze są liczbami całkowitymi przez całą drogę od bazy. Zamiana na złote jest ostatnią
// operacją przed wyświetleniem — nigdy przed sumowaniem, nigdy przed porównaniem.

const PLN = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const INT = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 });

export function formatGrosze(grosze: number | null | undefined): string {
  if (grosze === null || grosze === undefined || !Number.isFinite(grosze)) return '—';
  return PLN.format(grosze / 100);
}

const PLN_PELNE = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'PLN',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Kwota ZAOKRĄGLONA do pełnych złotych (rozliczenia: prowizja, VAT, wypłata) — bez „,00”,
 * żeby nie udawała dokładności, której nie ma. Kwota z groszami (np. obrót) trafiająca tu
 * przez pomyłkę NIE jest po cichu ucinana — wraca w pełnym formacie z groszami.
 */
export function formatZlote(grosze: number | null | undefined): string {
  if (grosze === null || grosze === undefined || !Number.isFinite(grosze)) return '—';
  if (grosze % 100 !== 0) return PLN.format(grosze / 100);
  return PLN_PELNE.format(grosze / 100);
}

/** Dokładna kwota dziesiętna z serwera („97.3201”) po polsku: „97,3201 zł”. Bez floatów. */
export function formatDokladnie(tekst: string | null | undefined): string {
  if (!tekst || !/^-?\d+\.\d+$/.test(tekst)) return '—';
  const [calkowite, ulamek] = tekst.split('.') as [string, string];
  const znak = calkowite.startsWith('-') ? '−' : '';
  const NBSP = String.fromCharCode(0xa0); // jak Intl pl-PL: twarda spacja w tysiącach i przed „zł”
  const cyfry = calkowite.replace('-', '');
  // Jak Intl.NumberFormat pl-PL: liczb czterocyfrowych się nie grupuje (1557), od pięciu — tak (12 345).
  const grupy = cyfry.length >= 5 ? cyfry.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP) : cyfry;
  return `${znak}${grupy},${ulamek}${NBSP}zł`;
}

/**
 * Odmiana rzeczownika przy liczbie: 1 rozliczenie, 2 rozliczenia, 5 rozliczeń, 22 rozliczenia,
 * 12 rozliczeń. `formy` = [jedna, kilka (2–4), wiele].
 */
export function odmiana(n: number, formy: readonly [string, string, string]): string {
  const [jedna, kilka, wiele] = formy;
  if (n === 1) return jedna;
  const d = Math.abs(n) % 10;
  const s = Math.abs(n) % 100;
  return d >= 2 && d <= 4 && (s < 12 || s > 14) ? kilka : wiele;
}

/** Stawka procentowa bez zbędnych zer: 0.19 → „19%”, 0.1725 → „17,25%”. */
export function formatStawka(ulamek: number | null | undefined): string {
  if (ulamek === null || ulamek === undefined || !Number.isFinite(ulamek)) return '—';
  return `${(Math.round(ulamek * 10_000) / 100).toLocaleString('pl-PL', { maximumFractionDigits: 2 })}%`;
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return INT.format(value);
}

/** Zmiana procentowa ze znakiem. `null` znaczy „nie da się policzyć”, a nie „0%”. */
export function formatDelta(pct: number | null | undefined): string {
  if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—';
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toLocaleString('pl-PL', { maximumFractionDigits: 1 })}%`;
}

export function formatRatio(ratio: number | null | undefined): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/** Zakres dat w formacie, który da się przeczytać na głos. */
export function formatRange(from: string, to: string): string {
  return from === to ? from : `${from} → ${to}`;
}

/** Domyślne okno analizy: ostatnie 30 dni licząc wstecz od dziś (czas warszawski). */
export function defaultRange(today = new Date()): { date_from: string; date_to: string } {
  const iso = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Europe/Warsaw' });
  const to = iso(today);
  const from = iso(new Date(today.getTime() - 29 * 86400000));
  return { date_from: from, date_to: to };
}
